import React from 'react';
import { motion } from 'framer-motion';

export interface SwotItem {
  text: string;
  impact: number; // 1-10 scale
  category: 'strength' | 'weakness' | 'opportunity' | 'threat';
}

interface SwotQuadrantChartProps {
  swotItems: SwotItem[];
  width?: number;
  height?: number;
}

const SwotQuadrantChart: React.FC<SwotQuadrantChartProps> = ({
  swotItems,
  width = 600,
  height = 400
}) => {
  // Color mapping for SWOT categories
  const categoryColors = {
    strength: '#34D399',    // Green
    weakness: '#F87171',    // Red
    opportunity: '#60A5FA', // Blue
    threat: '#FBBF24'       // Yellow
  };

  const categoryQuadrants = {
    strength: { x: 0, y: 0 },           // Top-left
    weakness: { x: width / 2, y: 0 },   // Top-right
    opportunity: { x: 0, y: height / 2 }, // Bottom-left
    threat: { x: width / 2, y: height / 2 }  // Bottom-right
  };

  const categoryLabels = {
    strength: 'Strengths',
    weakness: 'Weaknesses',
    opportunity: 'Opportunities',
    threat: 'Threats'
  };
  
  // Distribute items within their quadrants
  const getItemPosition = (item: SwotItem, index: number, sameTypeCount: number) => {
    const quadrant = categoryQuadrants[item.category];
    const quadrantWidth = width / 2;
    const quadrantHeight = height / 2;
    
    // Calculate bubble size to determine safe bounds
    const bubbleSize = Math.max(30, item.impact * 5);
    const padding = bubbleSize + 10; // Add padding to prevent cutoff
    
    // Spiral layout algorithm with safe bounds
    const angle = (index / sameTypeCount) * 2 * Math.PI; // Angle in radians
    const maxRadius = Math.min(quadrantWidth, quadrantHeight) / 2 - padding;
    const radius = maxRadius * 0.8 * (index / sameTypeCount + 0.3);
    
    const centerX = quadrant.x + (quadrantWidth / 2);
    const centerY = quadrant.y + (quadrantHeight / 2);
    
    let x = centerX + Math.cos(angle) * radius;
    let y = centerY + Math.sin(angle) * radius;
    
    // Constrain to safe bounds within quadrant
    x = Math.max(quadrant.x + padding, Math.min(quadrant.x + quadrantWidth - padding, x));
    y = Math.max(quadrant.y + padding, Math.min(quadrant.y + quadrantHeight - padding, y));
    
    return { x, y };
  };

  // Count items by category
  const itemCountByCategory: Record<string, number> = {};
  swotItems.forEach(item => {
    if (!itemCountByCategory[item.category]) {
      itemCountByCategory[item.category] = 0;
    }
    itemCountByCategory[item.category]++;
  });

  // Track indexes within each category
  const categoryIndexes: Record<string, number> = {
    strength: 0,
    weakness: 0,
    opportunity: 0,
    threat: 0
  };

  return (
    <div className="w-full" style={{ height: `${height}px` }}>
      <svg width={width} height={height}>
        {/* Draw quadrant dividers */}
        <line x1={width / 2} y1="0" x2={width / 2} y2={height} stroke="#666" strokeWidth="1" />
        <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke="#666" strokeWidth="1" />
        
        {/* Draw quadrant labels */}
        {Object.entries(categoryQuadrants).map(([category, position]) => (
          <text 
            key={category}
            x={position.x + (width / 4)} 
            y={position.y + 20}
            textAnchor="middle"
            fill="#666"
            fontWeight="bold"
            fontSize="16"
          >
            {categoryLabels[category as keyof typeof categoryLabels]}
          </text>
        ))}

        {/* Draw SWOT items */}
        {swotItems.map((item, index) => {
          const category = item.category;
          const categoryIndex = categoryIndexes[category]++;
          const position = getItemPosition(
            item, 
            categoryIndex, 
            itemCountByCategory[category]
          );
          
          // Bubble size based on impact
          const bubbleSize = Math.max(30, item.impact * 5);
          
          return (
            <motion.g 
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.1,
                type: 'spring',
                stiffness: 100
              }}
            >
              {/* Bubble */}
              <circle
                cx={position.x}
                cy={position.y}
                r={bubbleSize}
                fill={categoryColors[category]}
                opacity={0.7}
              />
              
              {/* Number in bubble */}
              <text
                x={position.x}
                y={position.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize={Math.max(14, Math.min(20, bubbleSize / 2.5))}
                fontWeight="bold"
                className="select-none"
                style={{ pointerEvents: 'none' }}
              >
                {categoryIndex + 1}
              </text>
            </motion.g>
          );
        })}
      </svg>
    </div>
  );
};

export default SwotQuadrantChart;
